import React, { useMemo } from "react";
import debounce from "lodash/debounce";
import { NextPage } from "next";
import Head from "next/head";
import Link from "next/link";
import Router, { useRouter } from "next/router";

import { getBrands } from "api/requests/getBrands";
import { Brand, BrandsResponse } from "custom-types/Brand";
import useDomain from "hooks/useDomain";
import { getDomainCountryCode } from "redux/selectors/config";
import { getCoordinates } from "redux/selectors/location";
import { getFirstQueryParam } from "utils/nextRouterUtils";

import Tab from "components/botanic/Tab";
import Tabs from "components/botanic/Tabs";
import BrandCoins from "components/brands/BrandCoins";
import Breadcrumb from "components/Breadcrumb";
import Breadcrumbs from "components/Breadcrumbs";
import PageNavigation from "components/PageNavigation";

type BrandIndexPageProps = {
  initialBrands: Array<Brand>;
  totalCount: number;
  hasAvailableNearby: boolean;
  isAllBrandsPage?: boolean;
};

const PER_PAGE = 60;

enum LocationFilter {
  ALL = "all",
  NEARBY = "nearby",
}

const BrandIndexPage: NextPage<BrandIndexPageProps> = ({
  initialBrands,
  totalCount,
  hasAvailableNearby,
  isAllBrandsPage = false,
}) => {
  const router = useRouter();
  const domain = useDomain();
  const searchTerm = router.query.search;
  const pageCount = Math.ceil(totalCount / PER_PAGE);
  const currentPage = Number(router.query.page || 1);

  const getURLForTab = (location: LocationFilter) => {
    return `/brands${location === LocationFilter.ALL ? "/all" : ""}`;
  };

  const nextPage = async () => {
    router.push({
      query: { ...router.query, page: currentPage + 1 },
    });
  };

  const prevPage = async () => {
    router.push({
      query: { ...router.query, page: currentPage - 1 },
    });
  };

  const search = debounce((event) => {
    router.push({
      query: {
        search: event.target.value,
      },
    });
  }, 300);

  const H1Text = useMemo(() => {
    if (searchTerm) {
      return `Showing results for ${searchTerm}`;
    }
    if (isAllBrandsPage) {
      return "All weed and CBD brands";
    }
    return "Weed and CBD brands near you";
  }, [searchTerm, isAllBrandsPage]);

  const currentPathForMeta = useMemo(() => {
    const { query, pathname } = router;

    const { page, ...queryWithoutPage } = query;

    const queryToUse = Number(page) > 1 ? query : queryWithoutPage;

    const queryString = new URLSearchParams(
      queryToUse as Record<string, string>
    ).toString();

    if (!queryString) {
      return pathname;
    }

    return pathname + "?" + queryString;
  }, [router.asPath]);

  return (
    <>
      <Head>
        <title>
          {isAllBrandsPage
            ? "All Weed and CBD Brands on Leafly"
            : "Shop Weed and CBD Brands Near You"}
        </title>
        <meta
          name="description"
          content={
            isAllBrandsPage
              ? "Find more information about established and emerging weed brands, CDB brands, cannabis growers, cart makers, and more on Leafly."
              : "Explore, find, and learn more about weed farms, weed pen brands, cbd brands, and other makers of the best cannabis products near you."
          }
        />
        {searchTerm && <meta name="robots" content="noindex" />}
        <link rel="canonical" href={`${domain}${currentPathForMeta}`} />
        <link
          href={`https://www.leafly.com${currentPathForMeta}`}
          hrefLang="en-us"
          rel="alternate"
        />
        <link
          href={`https://www.leafly.ca${currentPathForMeta}`}
          hrefLang="en-ca"
          rel="alternate"
        />
      </Head>
      <div className="bg-white">
        <Breadcrumbs>
          <Breadcrumb href="/brands" currentLocation>
            Brands
          </Breadcrumb>
        </Breadcrumbs>
        <div className="container">
          <div className="row">
            {initialBrands.length === 0 ? (
              <h1 className="p-sm">{`No results found for ${searchTerm}`}</h1>
            ) : (
              <h1 className="p-sm">{H1Text}</h1>
            )}
            <input
              id="searchbox"
              className="p-md m-sm"
              aria-label="Brand name search"
              placeholder="Brand name search"
              type="text"
              defaultValue={searchTerm}
              onChange={search}
            />
            {hasAvailableNearby && (
              <Tabs className="m-sm mt-lg" ariaLabel="Brands page">
                <Tab
                  active={!isAllBrandsPage}
                  href={getURLForTab(LocationFilter.NEARBY)}
                  as={Link}
                >
                  Available nearby
                </Tab>
                <Tab
                  active={isAllBrandsPage}
                  href={getURLForTab(LocationFilter.ALL)}
                  as={Link}
                >
                  All brands
                </Tab>
              </Tabs>
            )}
            <BrandCoins initialBrands={initialBrands} />
            <PageNavigation
              currentPage={currentPage}
              onNext={nextPage}
              onPrev={prevPage}
              totalPages={pageCount}
              className="pb-5"
            />
            <div className="text-center mt-8 mb-20 pt-0.5 pb-6">
              {isAllBrandsPage && (
                <>
                  Find information about all of the different weed farms, indoor
                  cannabis growers, weed pen brands, CBD gummy makers, and more.
                  Use Leafly to find a comprehensive list of weed and CBD brands
                  and manufacturers operating in the US and Canada.
                </>
              )}
              {!isAllBrandsPage && (
                <>
                  Browse the best and emerging cannabis growers, weed pen and
                  cart makers, CBD brands, and more on Leafly. Find{" "}
                  <Link href="/dispensaries" className="underline font-bold">
                    dispensaries
                  </Link>{" "}
                  that carry weed products from your favorite top-shelf weed
                  brands while discovering{" "}
                  <Link href="/deals" className="underline font-bold">
                    cannabis and CBD deals
                  </Link>{" "}
                  available near you.
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

BrandIndexPage.getInitialProps = async (context) => {
  const currentState = context.store.getState();
  const countryCode = getDomainCountryCode(currentState);
  const currentPage = Number(context.query.page || 1);

  const isAllBrandsPage = context.asPath?.includes("/all");

  const coordinates = getCoordinates(currentState);

  const searchTerm = getFirstQueryParam(context.query.search);
  const toSkip = (currentPage - 1) * PER_PAGE;

  const availableBrandsResponse = await getBrands({
    coordinates,
    countryCode,
    searchTerm,
    skip: toSkip,
    sort: "asc",
    take: PER_PAGE,
  });

  const hasAvailableNearby = Boolean(
    availableBrandsResponse.metadata.totalCount
  );

  if (!hasAvailableNearby && !isAllBrandsPage) {
    const queryString = new URLSearchParams(
      context.query as Record<string, string>
    ).toString();
    const newUrl = `/brands/all${queryString ? "?" + queryString : ""}`;
    if (context.res) {
      context.res.writeHead(302, {
        Location: newUrl,
      });
      context.res.end();
    } else {
      Router.replace(newUrl);
    }

    return {
      hasAvailableNearby,
      initialBrands: [],
      isAllBrandsPage,
      totalCount: 0,
    };
  }

  const shouldFetchAllBrands = !hasAvailableNearby || isAllBrandsPage;
  let allBrandsResponse = {
    data: [],
    metadata: { totalCount: 0 },
  } as BrandsResponse;
  if (shouldFetchAllBrands) {
    allBrandsResponse = await getBrands({
      countryCode,
      searchTerm,
      skip: toSkip,
      sort: "asc",
      take: PER_PAGE,
    });
  }

  return {
    hasAvailableNearby,
    initialBrands:
      hasAvailableNearby && !isAllBrandsPage
        ? availableBrandsResponse.data
        : allBrandsResponse.data,
    isAllBrandsPage,
    totalCount:
      hasAvailableNearby && !isAllBrandsPage
        ? availableBrandsResponse.metadata.totalCount
        : allBrandsResponse.metadata.totalCount,
  };
};

export default BrandIndexPage;
